import {
  effect,
  Injectable,
  makeStateKey,
  signal,
  StateKey,
  TransferState,
} from '@angular/core';
import { IEvent } from '../../../../server/interfaces/event';
import { config } from '../../../../shared/config';
import { SportType } from '../../../../shared/constants/sport-type';
import { ILocationCoordinate } from '../../../../shared/interfaces/location';
import { ApiResponse, HttpService } from './http.service';
import { NotificationService } from './notification.service';
import { SportFilterService } from './sport-filter.service';
import { PlatformService } from './platform.service';

@Injectable({
  providedIn: 'root',
})
export class EventService {
  filter = signal<string>('');
  lastFilter = signal<string>('');
  nearbyEvents = signal<IEvent[]>([]);
  gettingNearbyEvents = signal(false);
  endOfEventList = signal(false);

  event = signal<IEvent | null>(null);
  gettingEvent = signal<boolean>(false);

  initialized = signal(false);

  refreshEventRequired = signal(false);

  getNearbyEventTimeout = signal<any | null>(null);

  cursor = signal<number>(Date.now());

  constructor(
    private http: HttpService,
    private sportFilterService: SportFilterService,
    private notification: NotificationService,
    private transferState: TransferState,
    private platform: PlatformService
  ) {
    effect(() => {
      if (this.refreshEventRequired()) {
        this.refreshEventRequired.set(false);
        this.getEvent(this.event()?._id!);
      }
    });

    effect(() => {
      if (this.filter() !== this.lastFilter()) {
        this.lastFilter.set(this.filter());

        if (this.getNearbyEventTimeout()) {
          clearTimeout(this.getNearbyEventTimeout());
        }

        this.getNearbyEventTimeout.set(
          setTimeout(() => {
            this.getNearbyEvents(true);
          }, 500)
        );
      }
    });
  }

  /**
   * Get nearby events based on user location
   * @param initial Whether this is the initial request
   * @param coordinates User location coordinates
   * @param page Current page number
   * @param sportType Sport type filter
   */
  async getNearbyEvents(
    initial: boolean = false,
    coordinates?: ILocationCoordinate,
    page: number = 1,
    sportType: SportType = '' as SportType
  ): Promise<boolean | undefined> {
    this.initialized.set(true);

    this.nearbyEvents.set([]);
    this.endOfEventList.set(false);

    const stateKey: StateKey<number> = makeStateKey<number>(
      'transfer-get-nearby-events-cursor'
    );

    // If it's in TransferState (on client), use it and remove from TransferState
    if (this.transferState.hasKey(stateKey)) {
      const data = this.transferState.get<number>(stateKey, {} as number);
      this.transferState.remove(stateKey);
      this.cursor.set(data);
    } else if (initial) this.cursor.set(Date.now());

    const offset = (page ? page - 1 : 0) * config.dataTake;

    this.gettingNearbyEvents.set(true);
    const res = await this.http.get<IEvent[]>(`/event/nearby-by-coordinates`, {
      lat: coordinates?.coordinates[1] ?? '',
      long: coordinates?.coordinates[0] ?? '',
      sportType: sportType || this.sportFilterService.filter(),
      offset,
      filter: this.filter(),
      cursor: this.cursor(),
    });
    if (this.platform.isServer()) {
      this.transferState.set(stateKey, this.cursor());
    }
    this.gettingNearbyEvents.set(false);

    if (res.error) {
      this.notification.error(res.error);
      return;
    }

    if (res.data) {
      this.nearbyEvents.set(res.data);
      this.endOfEventList.set(res.data.length < config.dataTake);
    }

    return true;
  }

  /**
   * Get a single event by ID
   * @param id Event ID
   */
  async getEvent(id: string, coordinates?: ILocationCoordinate): Promise<void> {
    // if (this.gettingEvent()) return;

    this.gettingEvent.set(true);

    let res;

    const stateKey: StateKey<ApiResponse<IEvent>> = makeStateKey<
      ApiResponse<IEvent>
    >(`transfer-get-event-${id}`);

    // If it's in TransferState (on client), use it and remove from TransferState
    if (this.transferState.hasKey(stateKey)) {
      const data = this.transferState.get<ApiResponse<IEvent>>(
        stateKey,
        {} as ApiResponse<IEvent>
      );
      res = data;
      this.transferState.remove(stateKey);
    } else {
      res = await this.http.get<IEvent>(
        `/event/get-with-coordinates/${id}`,
        {
          lat: coordinates?.coordinates[1] ?? '',
          long: coordinates?.coordinates[0] ?? '',
        }
      );
    }

    if (this.platform.isServer()) {
      this.transferState.set(stateKey, res);
    }

    this.gettingEvent.set(false);

    if (res.data) {
      this.event.set(res.data);
    } else {
      this.event.set(null);
      this.notification.error(res.error);
    }
  }

  /**
   * Get member thumbnail based on gender
   */

  /**
   * Clear the events cache
   */
  clearCache(): void {
    this.nearbyEvents.set([]);
    this.event.set(null);
    this.endOfEventList.set(false);
    this.initialized.set(false);
  }
}
